import {Cohort, Model, PagedResponse} from "../../types";

export interface CohortTreeNode extends Cohort {
    children?: CohortTreeNode[]
}

export const buildCohortTree = (rawData?: PagedResponse | Model[]): CohortTreeNode[] => {
    const data = ((rawData as PagedResponse)?.results || rawData || []) as Cohort[];
    const rows = data.map((cohort) => ({...cohort})) as CohortTreeNode[];
    const byId = new Map<number, CohortTreeNode>();
    rows.forEach((row) => byId.set(row.id, row));

    const roots: CohortTreeNode[] = [];

    rows.forEach((row) => {
        const parent = row.parent_cohort ? byId.get(row.parent_cohort) : undefined;
        if (parent && parent.id !== row.id) {
            parent.children = [...(parent.children || []), row];
            return;
        }
        roots.push(row);
    });

    return roots;
};

export const buildCohortHierarchyOptions = (
    items: Cohort[],
    optionMap: (item: Cohort) => {value: string | number, label: string},
    prefix = '> '
) => {
    const tree = buildCohortTree(items as unknown as Model[]);
    const options: {value: string | number, label: string}[] = [];

    const walk = (node: CohortTreeNode, depth: number) => {
        const option = optionMap(node);
        options.push({
            ...option,
            label: `${prefix.repeat(depth)}${option.label}`
        });
        node.children?.forEach((child) => walk(child, depth + 1));
    };

    tree.forEach((root) => walk(root, 0));
    return options;
};

export const buildCohortTreeOptions = (
    items: Cohort[],
    optionMap: (item: Cohort) => {value: string | number, label: string}
) => {
    const tree = buildCohortTree(items as unknown as Model[]);

    const toOption = (node: CohortTreeNode): {value: string | number, label: string, children?: any[]} => {
        const option = optionMap(node);
        return {
            ...option,
            children: node.children?.map(toOption)
        };
    };

    return tree.map(toOption);
};
